/**
 * Subscription Service
 *
 * API service for handling subscription-related operations.
 * Communicates with backend endpoints for PayPal payments.
 *
 * @module services/subscription
 */

import api from './api';

/**
 * Response from upgrade-paypal endpoint (one-time payment)
 */
export interface UpgradePayPalResponse {
  success: boolean;
  message: string;
  subscription?: {
    type: string;
    expiresAt: string;
  };
}

/**
 * Response from activate-subscription endpoint (recurring subscription)
 */
export interface ActivateSubscriptionResponse {
  success: boolean;
  message: string;
  subscription?: {
    type: string;
    paypalSubscriptionId: string;
    expiresAt: string;
  };
}

/**
 * Response from subscription status endpoint
 */
export interface SubscriptionStatusResponse {
  subscriptionType: string;
  subscriptionExpiresAt: string | null;
  paypalSubscriptionId: string | null;
  isActive: boolean;
}

/**
 * Response from cancel subscription endpoint
 */
export interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
}

/**
 * Upgrade to Premium with a one-time PayPal payment (monthly)
 *
 * @param orderId - PayPal Order ID
 * @param captureId - PayPal Capture ID
 * @param amount - Payment amount
 * @param payerEmail - Payer's email (optional)
 * @returns Promise with upgrade result
 */
export async function upgradeWithPayPal(
  orderId: string,
  captureId: string,
  amount: string,
  payerEmail?: string
): Promise<UpgradePayPalResponse> {
  const response = await api.post<UpgradePayPalResponse>(
    '/subscription/upgrade-paypal',
    {
      orderId,
      captureId,
      amount,
      payerEmail,
    }
  );
  return response.data;
}

/**
 * Activate a recurring PayPal subscription (annual)
 *
 * @param subscriptionId - PayPal Subscription ID
 * @param planId - PayPal Plan ID
 * @param payerEmail - Payer's email (optional)
 * @returns Promise with activation result
 */
export async function activateSubscription(
  subscriptionId: string,
  planId: string,
  payerEmail?: string
): Promise<ActivateSubscriptionResponse> {
  const response = await api.post<ActivateSubscriptionResponse>(
    '/subscription/activate-subscription',
    {
      subscriptionId,
      planId,
      payerEmail,
    }
  );
  return response.data;
}

/**
 * Get current subscription status
 *
 * @returns Promise with subscription status
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
  const response = await api.get<SubscriptionStatusResponse>(
    '/subscription/status'
  );
  return response.data;
}

/**
 * Subscription details response
 */
export interface SubscriptionDetailsResponse {
  success: boolean;
  data: {
    plan: 'free' | 'premium' | 'admin';
    status: 'active' | 'cancelled' | 'expired' | 'free';
    isActive: boolean;
    isAdmin: boolean;
    isSecondaryUser?: boolean;
    ownerEmail?: string | null;
    memberSince: string;
    subscriptionStartDate: string | null;
    subscriptionEndDate: string | null;
    daysRemaining: number | null;
    paymentType: string | null;
    isRecurring: boolean;
    paypalSubscriptionId: string | null;
    canCancel: boolean;
    pricing: {
      monthlyPrice: number;
      annualPrice: number;
      annualMonthlyEquivalent: number;
    };
  };
}

/**
 * Payment history item
 */
export interface PaymentHistoryItem {
  id: string;
  orderId: string;
  captureId: string | null;
  subscriptionId: string | null;
  planId: string | null;
  amount: number;
  currency: string;
  paymentType: string;
  status: string;
  statusLabel: string;
  payerEmail: string | null;
  createdAt: string;
}

/**
 * Payment history response
 */
export interface PaymentHistoryResponse {
  success: boolean;
  data: {
    payments: PaymentHistoryItem[];
    totalPayments: number;
    totalPaid: number;
    currency: string;
  };
}

/**
 * Get detailed subscription information
 *
 * @returns Promise with subscription details
 */
export async function getSubscriptionDetails(): Promise<SubscriptionDetailsResponse> {
  const response = await api.get<SubscriptionDetailsResponse>(
    '/subscription/details'
  );
  return response.data;
}

/**
 * Get payment history
 *
 * @returns Promise with payment history
 */
export async function getPaymentHistory(): Promise<PaymentHistoryResponse> {
  const response = await api.get<PaymentHistoryResponse>(
    '/subscription/payments'
  );
  return response.data;
}

/**
 * Cancel current subscription
 *
 * @returns Promise with cancellation result
 */
export async function cancelSubscription(): Promise<CancelSubscriptionResponse> {
  const response = await api.post<CancelSubscriptionResponse>(
    '/subscription/cancel'
  );
  return response.data;
}

export default {
  upgradeWithPayPal,
  activateSubscription,
  getSubscriptionStatus,
  cancelSubscription,
};
